import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package, Clock, CheckCircle, Truck, MapPin, Calendar, Users, Eye, Loader2, AlertCircle, Plus, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Lazy-initialize customer from sessionStorage to avoid setState inside useEffect.
  const [customer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('customer');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed.email ? parsed : null;
    } catch {
      return null;
    }
  });
  const [tripRequests, setTripRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runSearch = useCallback(async (searchEmail) => {
    if (!/\S+@\S+\.\S+/.test(searchEmail)) {
      setError(t('errors.validEmail'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/trip-requests`);
      const allRequests = response.data;

      const customerRequests = allRequests.filter(
        (req) => req.email.toLowerCase() === searchEmail.toLowerCase()
      );

      const requestsWithQuotes = await Promise.all(
        customerRequests.map(async (req) => {
          try {
            const quotesResponse = await axios.get(`${API}/quotes/trip/${req.request_id}`);
            return {
              ...req,
              quotes: quotesResponse.data,
              quoteCount: quotesResponse.data.length,
            };
          } catch {
            return { ...req, quotes: [], quoteCount: 0 };
          }
        })
      );

      requestsWithQuotes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTripRequests(requestsWithQuotes);
    } catch (err) {
      setError(t('errors.fetchFailed'));
      setTripRequests([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // If not signed in, redirect to login. Otherwise load trips on mount.
  useEffect(() => {
    if (!customer) {
      navigate('/customer/login?redirect=/dashboard', { replace: true });
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      await runSearch(customer.email);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [customer, navigate, runSearch]);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'paid':
        return { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CreditCard, label: t('status.paid') || 'Paid' };
      case 'confirmed':
        return { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: t('status.confirmed') };
      case 'in_progress':
        return { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Truck, label: t('status.inProgress') };
      case 'quoted':
        return { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Package, label: t('status.quoted') };
      case 'completed':
        return { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle, label: t('status.completed') };
      default:
        return { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock, label: t('status.pending') };
    }
  };

  const getAcceptedQuote = (request) => {
    if (!request.quotes || !request.accepted_quote_id) return null;
    return request.quotes.find(q => q.quote_id === request.accepted_quote_id);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.backToHome')}</span>
          </button>

          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {customer?.first_name ? (
                <>
                  {t('common.welcomeBack') || 'Welcome back'},{' '}
                  <span className="text-amber-500">{customer.first_name}</span>
                </>
              ) : (
                <>
                  {t('customer.dashboard').split(' ')[0]}{' '}
                  <span className="text-amber-500">
                    {t('customer.dashboard').split(' ').slice(1).join(' ')}
                  </span>
                </>
              )}
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('customer.dashboardSubtitle')}
            </p>

            {/* Primary CTA — moved to the top of the dashboard */}
            <div className="mt-8 flex justify-center">
              <Button
                onClick={() => navigate('/trip-form')}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25"
                data-testid="dashboard-new-trip-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('customer.requestNewTransport')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <section className="pb-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-500" data-testid="error-message">{error}</p>
            </div>
          </div>
        </section>
      )}

      {/* Loading state while trips fetch */}
      {loading && (
        <section className="pb-12 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center py-16" data-testid="dashboard-loading">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        </section>
      )}

      {/* Results Section */}
      {customer && !loading && (
        <section className="pb-24 px-4">
          <div className="max-w-4xl mx-auto">
            {tripRequests.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center" data-testid="no-trips-message">
                <Package className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">{t('customer.noTripsFound')}</h3>
                <p className="text-gray-400 mb-6">
                  {t('customer.noTripsMessage')}
                </p>
                <Button
                  onClick={() => navigate('/trip-form')}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-4 rounded-full"
                >
                  {t('customer.requestQuote')}
                </Button>
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{tripRequests.length}</p>
                    <p className="text-gray-400 text-sm">{t('customer.totalRequests')}</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-amber-400">
                      {tripRequests.filter(r => r.status === 'pending').length}
                    </p>
                    <p className="text-gray-400 text-sm">{t('customer.pending')}</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">
                      {tripRequests.filter(r => r.status === 'quoted').length}
                    </p>
                    <p className="text-gray-400 text-sm">{t('customer.haveQuotes')}</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-400">
                      {tripRequests.filter(r => ['paid', 'confirmed', 'in_progress', 'completed'].includes(r.status)).length}
                    </p>
                    <p className="text-gray-400 text-sm">{t('customer.myBookings') || 'My Bookings'}</p>
                  </div>
                </div>

                {(() => {
                  const BOOKING_STATUSES = ['paid', 'confirmed', 'in_progress', 'completed'];
                  const bookings = tripRequests.filter(r => BOOKING_STATUSES.includes(r.status));
                  const requests = tripRequests.filter(r => !BOOKING_STATUSES.includes(r.status));

                  const renderTripCard = (request) => {
                    const statusConfig = getStatusConfig(request.status);
                    const StatusIcon = statusConfig.icon;
                    const acceptedQuote = getAcceptedQuote(request);
                    const isBooking = BOOKING_STATUSES.includes(request.status);

                    return (
                      <div
                        key={request.id}
                        className={`bg-white/5 border rounded-2xl p-6 transition-all duration-300 hover:border-amber-500/50 ${
                          isBooking
                            ? 'border-emerald-500/30'
                            : 'border-white/10'
                        }`}
                        data-testid={`trip-card-${request.request_id}`}
                      >
                        <div className="flex flex-col lg:flex-row justify-between gap-4">
                          {/* Trip Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                              <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-mono">
                                {request.request_id}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                                <StatusIcon className="w-4 h-4" />
                                {statusConfig.label}
                              </span>
                              {request.quoteCount > 0 && !isBooking && (
                                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                                  {request.quoteCount} quote{request.quoteCount !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                              <div className="space-y-2">
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Pick-up</p>
                                    <p className="text-white">{request.pickup_address}</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Drop-off</p>
                                    <p className="text-white">{request.dropoff_address}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Transport Date</p>
                                    <p className="text-white">{request.transport_date}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Users className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                  <div>
                                    <p className="text-gray-400 text-xs">Horses</p>
                                    <p className="text-white">{request.horse_count}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Accepted Quote Info */}
                            {acceptedQuote && (
                              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Truck className="w-5 h-5 text-green-400" />
                                  <span className="text-green-400 font-semibold">
                                    {request.status === 'paid' ? 'Paid Transporter' : 'Confirmed Transporter'}
                                  </span>
                                </div>
                                <div className="grid md:grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-400">Company</p>
                                    <p className="text-white font-medium">{acceptedQuote.transporter_company}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Service Type</p>
                                    <p className="text-white font-medium capitalize">{acceptedQuote.service_type}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400">Total Price (Gross)</p>
                                    <p className="text-amber-500 font-bold">MAD {acceptedQuote.customer_total?.toFixed(2)}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex lg:flex-col gap-2 lg:w-40">
                            {isBooking ? (
                              <Button
                                onClick={() => navigate(`/my-quotes?request_id=${request.request_id}&email=${customer?.email || ''}`)}
                                variant="outline"
                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-xl flex-1"
                                data-testid={`view-booking-btn-${request.request_id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                {t('customer.viewBooking')}
                              </Button>
                            ) : request.quoteCount > 0 ? (
                              <Button
                                onClick={() => navigate(`/my-quotes?request_id=${request.request_id}&email=${customer?.email || ''}`)}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl flex-1"
                                data-testid={`view-quotes-btn-${request.request_id}`}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                {t('customer.viewQuotes')} ({request.quoteCount})
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                className="border-white/20 text-gray-400 rounded-xl flex-1"
                                disabled
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                {t('customer.awaitingQuotes')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      {bookings.length > 0 && (
                        <div className="mb-10" data-testid="bookings-section">
                          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-emerald-400" />
                            {t('customer.myBookings') || 'My Bookings'}
                            <span className="text-sm text-gray-400 font-normal">({bookings.length})</span>
                          </h2>
                          <div className="space-y-4" data-testid="bookings-list">
                            {bookings.map(renderTripCard)}
                          </div>
                        </div>
                      )}

                      {requests.length > 0 && (
                        <div data-testid="requests-section">
                          <h2 className="text-xl font-bold text-white mb-4">
                            {bookings.length > 0 ? (t('customer.openRequests') || 'Open Requests') : t('customer.yourTrips')}
                            <span className="text-sm text-gray-400 font-normal ml-2">({requests.length})</span>
                          </h2>
                          <div className="space-y-4" data-testid="trips-list">
                            {requests.map(renderTripCard)}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
